///
/// EarthBrowser Admin - GeoPackage API
///
/// Created by Matt Giger on December 15, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { authenticatedApi } from './authenticatedApi'
import type {
  GeoPackageCatalog,
  CreateGeoPackageRequest,
  UpdateGeoPackageRequest
} from '@shared-types/geopackage-catalog'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.earthbrowser.com'

// Maximum upload size: 200MB
const MAX_UPLOAD_SIZE = 200 * 1024 * 1024

export interface UploadProgressEvent {
  loaded: number
  total: number
  percentage: number
}

export const geopackageApi = authenticatedApi.injectEndpoints({
  endpoints: (builder) => ({
    // List all packages (admin view)
    adminListGeoPackages: builder.query<{ packages: GeoPackageCatalog[]; total: number }, {
      limit?: number
      offset?: number
      status?: string
    }>({
      query: ({ limit = 50, offset = 0, status }) => {
        const params = new URLSearchParams()
        params.set('limit', String(limit))
        params.set('offset', String(offset))
        if (status) params.set('status', status)
        return `/api/admin/geopackages?${params}`
      },
      providesTags: ['AdminGeoPackages'],
    }),

    // Get upload URL for direct browser upload to Backblaze B2
    getUploadUrl: builder.mutation<{
      uploadUrl: string
      authorizationToken: string
      bucketId: string
    }, void>({
      query: () => ({
        url: '/api/admin/geopackages/upload-url',
        method: 'POST',
      }),
    }),

    // Upload GeoPackage file directly to Backblaze B2
    // This uses the Native B2 API for direct browser uploads, bypassing the API server
    // See: https://www.backblaze.com/docs/cloud-storage-upload-files-with-the-native-api
    uploadGeoPackage: builder.mutation<{
      success: boolean
      file_url: string
      file_size: number
      file_hash: string
      compressed: boolean
    }, {
      file: File
      version: string
      compress?: boolean
      onProgress?: (progress: UploadProgressEvent) => void
    }>({
      queryFn: async ({ file, version, compress = true, onProgress }) => {
        try {
          // Validate file size (200MB max)
          if (file.size > MAX_UPLOAD_SIZE) {
            const sizeMB = (file.size / (1024 * 1024)).toFixed(2)
            const maxSizeMB = (MAX_UPLOAD_SIZE / (1024 * 1024)).toFixed(0)
            throw new Error(`File size (${sizeMB}MB) exceeds maximum upload size of ${maxSizeMB}MB`)
          }

          // Step 1: Get upload URL from our API server
          const uploadUrlResponse = await fetch(`${API_URL}/api/admin/geopackages/upload-url`, {
            method: 'POST',
            credentials: 'include',
          })

          if (!uploadUrlResponse.ok) {
            throw new Error('Failed to get upload URL')
          }

          const uploadUrlData = await uploadUrlResponse.json() as { uploadUrl: string; authorizationToken: string; bucketId: string }
          const { uploadUrl, authorizationToken } = uploadUrlData

          // Step 2: Calculate SHA1 hash (required by B2)
          const arrayBuffer = await file.arrayBuffer()
          const hashBuffer = await crypto.subtle.digest('SHA-1', arrayBuffer)
          const hashArray = Array.from(new Uint8Array(hashBuffer))
          const sha1 = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

          // Step 3: Upload directly to Backblaze B2 with progress tracking
          // Create versioned filename: filename-v1.0.0.gpkg
          const fileNameWithoutExt = file.name.replace('.gpkg', '')
          const versionedFileName = `${fileNameWithoutExt}-v${version}.gpkg`
          const fileName = `geopackages/${versionedFileName}`

          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest()

            // Track upload progress
            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable && onProgress) {
                onProgress({
                  loaded: e.loaded,
                  total: e.total,
                  percentage: (e.loaded / e.total) * 100
                })
              }
            })

            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve()
              } else {
                try {
                  const error = JSON.parse(xhr.responseText)
                  reject(new Error(error.message || 'Upload to B2 failed'))
                } catch {
                  reject(new Error(`Upload failed: ${xhr.statusText}`))
                }
              }
            })

            xhr.addEventListener('error', () => {
              reject(new Error('Network error during upload to B2'))
            })

            xhr.addEventListener('abort', () => {
              reject(new Error('Upload cancelled'))
            })

            // Upload to Backblaze B2 using their Native API
            xhr.open('POST', uploadUrl)
            xhr.setRequestHeader('Authorization', authorizationToken)
            xhr.setRequestHeader('X-Bz-File-Name', fileName)
            xhr.setRequestHeader('Content-Type', 'application/geopackage+sqlite3')
            xhr.setRequestHeader('X-Bz-Content-Sha1', sha1)
            if (compress) {
              xhr.setRequestHeader('X-Bz-Info-Content-Encoding', 'br')
            }
            xhr.send(file)
          })

          // Step 4: Notify our API server that upload is complete
          const completeResult = await fetch(`${API_URL}/api/admin/geopackages/upload-complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              fileName: versionedFileName,
              fileSize: file.size,
              fileHash: sha1,
              compressed: compress
            })
          })

          if (!completeResult.ok) {
            throw new Error('Failed to complete upload')
          }

          const completeData = await completeResult.json()

          return { data: completeData }
        } catch (error: any) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              error: error.message || 'Upload failed',
              data: error
            }
          }
        }
      },
      invalidatesTags: ['AdminGeoPackages'],
    }),

    // Create package metadata
    createGeoPackageMetadata: builder.mutation<GeoPackageCatalog, CreateGeoPackageRequest>({
      query: (body) => ({
        url: '/api/admin/geopackages',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AdminGeoPackages'],
    }),

    // Update package
    adminUpdateGeoPackage: builder.mutation<GeoPackageCatalog, { id: string; body: UpdateGeoPackageRequest }>({
      query: ({ id, body }) => ({
        url: `/api/admin/geopackages/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AdminGeoPackages'],
    }),

    // Delete package
    adminDeleteGeoPackage: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/api/admin/geopackages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminGeoPackages'],
    }),

    // Get package stats
    getGeoPackageStats: builder.query<{
      total_packages: number
      total_downloads: number
      total_size_bytes: number
      by_category: Array<{ category: string; count: number }>
      by_status: Array<{ status: string; count: number }>
    }, void>({
      query: () => '/api/admin/geopackages/stats',
      providesTags: ['AdminGeoPackages'],
    }),
  }),
})

export const {
  useAdminListGeoPackagesQuery,
  useUploadGeoPackageMutation,
  useCreateGeoPackageMetadataMutation,
  useAdminUpdateGeoPackageMutation,
  useAdminDeleteGeoPackageMutation,
  useGetGeoPackageStatsQuery,
} = geopackageApi

